import React from "react";
import { Link } from "react-router-dom";
import { featuredGuide } from "@/data/seasonal";

/**
 * A plain, factual summary near the top of the homepage. Search engines and
 * AI answers (Google AI Overviews, ChatGPT) quote short definitional passages
 * like this, so keep every line accurate and specific — no claims the stores
 * don't back up.
 */
const facts: { label: string; value: React.ReactNode }[] = [
  { label: "What it does", value: "Helps couples, friends and families agree on a movie or TV show to watch" },
  { label: "How it works", value: "Everyone swipes through trailers on their own phone; titles two or more people say yes to become matches" },
  { label: "Platforms", value: "iPhone, iPad and Android. iPhone and Android users can use it together" },
  { label: "Price", value: "Free to download and use, with 15 swipes a day on the free plan. Optional ReelMatch Pro removes the limit and adds streaming-service and genre filters and instant launch on a smart TV" },
  { label: "Streaming services", value: "Covers titles on Netflix, Prime Video, Disney+, Hulu, Apple TV+ and Max. ReelMatch doesn't stream anything itself" },
  { label: "Data", value: "Movie and TV data from TMDB, trailers from YouTube" },
];

const guideLinks = [
  { to: "/guides/movies-to-watch-as-a-couple", text: "Movies to watch with your boyfriend or girlfriend" },
  { to: "/guides/family-movies-to-watch", text: "Family movies to watch" },
  { to: "/guides/movies-to-watch-with-friends", text: "Movies to watch with friends" },
  { to: "/guides/movies-to-watch-with-teens", text: "Good movies to watch with your teen" },
  { to: "/guides/movies-to-watch-with-your-mom", text: "Movies to watch with your mom" },
  { to: "/guides/feel-good-movies", text: "Feel-good movies to watch" },
  { to: "/guides/family-christmas-movies", text: "Family Christmas movies" },
  { to: "/guides/thanksgiving-movies", text: "Thanksgiving movies" },
  { to: "/guides/how-to-decide-what-to-watch-with-your-partner", text: "How to decide what to watch with your partner" },
  { to: "/guides/how-to-pick-a-movie-for-a-group", text: "How to pick a movie for a group" },
  { to: "/guides/how-movie-matching-apps-work", text: "How movie matching apps work" },
];

const AtAGlance: React.FC = () => (
  <section id="what-is-reelmatch" className="section-container">
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 max-w-6xl mx-auto">
      <div className="lg:col-span-3">
        <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">What is ReelMatch?</h2>
        <p className="text-lg text-reelmatch-gray leading-relaxed mb-6">
          ReelMatch is a free movie matcher and movie picker for couples, friends and families on iPhone and Android. You and
          your partner, friends or family each swipe through movie and TV
          trailers on your own phone, and ReelMatch shows the titles you all
          said yes to, so you can stop scrolling and start watching.
        </p>
        <dl className="divide-y divide-gray-200 border-y border-gray-200">
          {facts.map((fact) => (
            <div key={fact.label} className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-3">
              <dt className="font-medium text-reelmatch-dark">{fact.label}</dt>
              <dd className="sm:col-span-2 text-reelmatch-gray">{fact.value}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="lg:col-span-2">
        <h2 className="text-2xl font-display font-bold mb-4">Picking something tonight?</h2>
        <p className="mb-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-reelmatch-primary">
            {featuredGuide.label}
          </span>
          <br />
          <Link
            to={`/guides/${featuredGuide.slug}`}
            className="text-lg font-semibold underline underline-offset-4 hover:text-reelmatch-primary transition-colors"
          >
            {featuredGuide.text}
          </Link>
        </p>
        <p className="mb-4">
          <Link
            to="/random-movie-generator"
            className="font-semibold underline underline-offset-4 hover:text-reelmatch-primary transition-colors"
          >
            Can't decide? Try the random movie generator
          </Link>
        </p>
        <p className="mb-4 font-semibold">
          What to watch on{" "}
          <Link to="/movies/what-to-watch-on-netflix" className="underline underline-offset-4 hover:text-reelmatch-primary transition-colors">Netflix</Link>,{" "}
          <Link to="/movies/what-to-watch-on-disney-plus" className="underline underline-offset-4 hover:text-reelmatch-primary transition-colors">Disney+</Link> and{" "}
          <Link to="/movies/what-to-watch-on-prime-video" className="underline underline-offset-4 hover:text-reelmatch-primary transition-colors">Prime Video</Link>
        </p>
        <ul className="space-y-3">
          {guideLinks.map((guide) => (
            <li key={guide.to}>
              <Link
                to={guide.to}
                className="font-medium underline underline-offset-4 hover:text-reelmatch-primary transition-colors"
              >
                {guide.text}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

export default AtAGlance;
